import { useState } from "react";
import { Button, Form, Icon, Input, Popup, Select } from "semantic-ui-react";
import styled from "styled-components";
import CallLogs from "../components/CallLogs";
import DialPad from "../components/DialPad";
import SideScrollerLayout, {
  SideScrollerContent,
  SideScrollerSidebar,
  SideScrollerTitle,
} from "../components/SideScrollerLayout";
import StatCard from "../components/StatCard";
import { Card } from "../components/ui";
import MeetNowButton from "../components/primum/MeetNowButton";
import { useAuth } from "../contexts/AuthContext";
import { useCalls } from "../contexts/CallsContext";
import { useUserClis } from "../contexts/UserClisContext";
import useCallInsights from "../hooks/useCallInsights";
import layout from "../layout";
import devices from "../layout/devices";
import { colours } from "./Insights";

const Content = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;
  height: calc(100vh - ${layout.topbarHeight});
  justify-content: center;
  align-items: flex-start;
  flex-wrap: wrap;
  box-sizing: border-box;
  padding: 6vh 16px 32px;
  overflow-y: auto;

  @media ${devices.laptopL} {
    padding-top: 14vh;
  }
`;

const style = {
  dialPadContainer: {
    margin: "auto",
    textAlign: "center",
    width: "245px",
  },
  dialPadInput: {
    marginBottom: "12px",
    height: "46px",
  },
  dialPadCall: {
    marginTop: "12px",
    marginBottom: "12px",
    height: "46px",
  },
  unparkCall: {
    marginBottom: "12px",
  },
};

function Calls() {
  const [destinationNumber, setDestinationNumber] = useState("");
  const { hasFeature } = useAuth();
  const { userClis, selectedCli, setSelectedCli } = useUserClis();

  const { isPhoneControl } = useAuth();
  const { handleMakeCall, handleUnparkCall } = useCalls();

  const handleDialPad = (value) => {
    setDestinationNumber(destinationNumber + value);
  };

  return (
    <SideScrollerLayout>
      <SideScrollerSidebar>
        <SideScrollerTitle>Call History</SideScrollerTitle>
        <CallLogs />
      </SideScrollerSidebar>

      <SideScrollerContent>
        <Content>
          <Card style={{ padding: "20px" }}>
            <Form
              style={style.dialPadContainer}
              onSubmit={() => {
                handleMakeCall(destinationNumber, "audio");
                setDestinationNumber("");
              }}
            >
              {userClis.length > 0 && hasFeature("cli_selection") && (
                <Form.Field>
                  <label for="cli">Outbound CLI</label>
                  <Select
                    placeholder="User default"
                    name="cli"
                    value={selectedCli}
                    onChange={(ev, el) => {
                      setSelectedCli(el.value);
                    }}
                    options={[
                      {
                        key: null,
                        value: null,
                        text: "User default",
                      },
                      ...userClis.map((c) => ({
                        key: c.id,
                        value: c.id,
                        text:
                          c.cliNumber + (c.cliLabel ? ` (${c.cliLabel})` : ""),
                      })),
                    ]}
                  />
                </Form.Field>
              )}

              <Input
                placeholder="Dial"
                fluid
                autoFocus
                value={destinationNumber}
                onChange={(e) =>
                  setDestinationNumber(e.target.value.replace(/\D/g, ""))
                }
                action={
                  <Button
                    icon
                    type="button"
                    color="teal"
                    disabled={!destinationNumber}
                    onClick={() => {
                      handleMakeCall(destinationNumber, "audio");
                      setDestinationNumber("");
                    }}
                  >
                    <Icon name="phone" />
                  </Button>
                }
                style={style.dialPadInput}
              />

              <DialPad onClick={handleDialPad} />

              <Button
                fluid
                type="submit"
                style={style.dialPadCall}
                disabled={destinationNumber.length === 0}
                color="teal"
                icon="phone"
              />
            </Form>

            {!isPhoneControl && (
              <Popup
                position="bottom center"
                disabled={destinationNumber.length === 3}
                content={"Park locations are 3 digits long"}
                trigger={
                  <span>
                    <Button
                      fluid
                      basic
                      style={style.unparkCall}
                      disabled={destinationNumber.length !== 3}
                      primary
                      onClick={() => handleUnparkCall(destinationNumber)}
                    >
                      <Icon name="phone" />
                      Unpark Call
                    </Button>
                  </span>
                }
              />
            )}

            <MeetNowButton />

            <Voicemail />
          </Card>

          {hasFeature(["insights"]) && (
            <div>
              <Insights />
            </div>
          )}
        </Content>
      </SideScrollerContent>
    </SideScrollerLayout>
  );
}

function Insights() {
  const { callInsights, getUpdatedAt } = useCallInsights();

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "row",
        gap: "15px",
        flexWrap: "wrap",
        justifyContent: "center",
        margin: "40px",
      }}
    >
      <div>
        <StatCard
          label="Recent calls"
          value={callInsights?.count}
          colour={colours.purple}
          loading={!callInsights}
          help="Up to a maximum of 1000"
        />
        <StatCard
          label="Average Talk Time"
          value={callInsights?.average_duration}
          colour={colours.purple}
          loading={!callInsights}
          help="Displayed as minutes;seconds"
        />
        <StatCard
          label="Total Talk Time"
          value={callInsights?.total_duration}
          colour={colours.purple}
          loading={!callInsights}
          help="Displayed as minutes;seconds"
        />
      </div>
      <div>
        <StatCard
          label="Recent Outgoing Calls"
          value={callInsights?.outgoing?.count}
          colour={colours.violet}
          loading={!callInsights}
        />
        <StatCard
          label="Recent Outgoing %"
          value={callInsights?.outgoing?.percent}
          colour={colours.violet}
          loading={!callInsights}
        />
        <StatCard
          label="Recent Incoming Calls"
          value={callInsights?.count}
          colour={colours.teal}
          loading={!callInsights}
        />
        <StatCard
          label="Recent Incoming %"
          value={callInsights?.incoming?.percent}
          colour={colours.teal}
          loading={!callInsights}
        />
        <p>
          <small>Last refreshed at {getUpdatedAt()}</small>
        </p>
      </div>
    </div>
  );
}

function Voicemail() {
  const { vmInfo, getVmLink } = useCalls();

  const [openingVm, setOpeningVm] = useState(false);

  const handleClick = async () => {
    setOpeningVm(true);
    const res = await getVmLink();
    await getVmLink();
    setOpeningVm(false);
    window.open(res.vmDesktopWeb);
  };

  if (!vmInfo) return null;

  return (
    <Button
      fluid
      color="blue"
      basic={vmInfo?.newMsg === 0}
      onClick={handleClick}
      disabled={openingVm}
      loading={openingVm}
    >
      <Icon name="film" />{" "}
      {vmInfo?.newMsg > 0
        ? `${vmInfo.newMsg} new VM messages`
        : "Access Voicemail"}
    </Button>
  );
}

export default Calls;
